import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';
import { toast } from 'react-toastify';

const OrderEditModal = ({ 
  isOpen, 
  onClose,
  orderData, 
  onEditComplete,
  currentUser
}) => {

  // Use the API_URL from parent component or fallback
  const API_URL = API_CONFIG.API_URL;

  const [activeTab, setActiveTab] = useState('repairs'); // 'repairs' or 'tasks'
  const [editData, setEditData] = useState({
    repairItems: [],
    newTasks: [],
    notes: '',
    customRepair: { description: '', price: '' }
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [repairOptions, setRepairOptions] = useState([]);
  const [repairSearchQuery, setRepairSearchQuery] = useState('');
  const [roomsData, setRoomsData] = useState({
    Pomona: { rooms: [], tasks: {}, workers: {} },
    Lakewood: { rooms: [], tasks: {}, workers: {} },
    Israel: { rooms: [], tasks: {}, workers: {} },
    China: { rooms: [], tasks: {}, workers: {} }
  });

  // Fetch repair options and rooms data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchRepairOptions();
      fetchRoomsData();
    }
  }, [isOpen]);

  const fetchRepairOptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/get-repair-options`);
      if (response.ok) {
        const data = await response.json();
        setRepairOptions(data);
      }
    } catch (error) {
      console.error('Error fetching repair options:', error);
    }
  };

  const fetchRoomsData = async () => {
    const locations = ['Pomona', 'Lakewood', 'Israel', 'China'];
    const newRoomsData = { ...roomsData };
    
    for (const location of locations) {
      try {
        const response = await fetch(`${API_URL}/api/get-rooms/${location}`);
        if (response.ok) {
          const rooms = await response.json();
          newRoomsData[location].rooms = rooms.map(room => ({
            roomNo: room.RoomNo,
            roomName: `${room.RoomNo} ${room.RoomDescription}`,
            parentRoomNo: room.ParentRoomNo
          }));
        }
      } catch (error) {
        console.error(`Error fetching rooms for ${location}:`, error);
      }
    }
    
    setRoomsData(newRoomsData);
  };

  const addRepairItem = (repair) => {
    const newRepair = {
      id: Date.now(),
      type: 'repair',
      description: repair.RepairDescription,
      price: repair.RepairCost,
      quantity: 1
    };
    
    setEditData(prev => ({
      ...prev,
      repairItems: [...prev.repairItems, newRepair]
    }));
  };

  const removeRepairItem = (itemId) => {
    setEditData(prev => ({
      ...prev,
      repairItems: prev.repairItems.filter(item => item.id !== itemId)
    }));
  };

  // Filter repair options based on search query
  const filteredRepairOptions = repairOptions.filter(repair => {
    if (!repairSearchQuery.trim()) return true;

    const searchLower = repairSearchQuery.toLowerCase();
    const descriptionMatch = repair.RepairDescription?.toLowerCase().includes(searchLower);
    const priceMatch = repair.RepairCost?.toString().includes(searchLower);

    return descriptionMatch || priceMatch;
  });

  const addNewTask = () => {
    const newTask = {
      id: Date.now(),
      roomNo: '',
      roomTask: '',
      assignedWorker: '',
      taskDueDate: '',
      taskNotes: '',
      location: 'Pomona',
      status: 'Not Started'
    };
    
    setEditData(prev => ({
      ...prev,
      newTasks: [...prev.newTasks, newTask]
    }));
  };

  const updateNewTask = (taskId, updates) => {
    setEditData(prev => ({
      ...prev,
      newTasks: prev.newTasks.map(task => 
        task.id === taskId ? { ...task, ...updates } : task
      )
    }));
  };

  const handleRoomChange = async (taskId, location, roomNo) => {
    console.log('=== ROOM CHANGE DEBUG ===');
    console.log('Changing to location:', location, 'room:', roomNo);
    
    // Update the task with new location and room
    updateNewTask(taskId, { 
      location, 
      roomNo,
      roomTask: '', // Clear task when room changes
      assignedWorker: '' // Clear worker when room changes
    });
    
    // Load the room data if we have both location and room
    if (location && roomNo) {
      console.log('Loading room data for:', location, roomNo);
      await loadRoomDataInBackground(location, roomNo);
    }
  };

  const loadRoomDataInBackground = async (location, roomNo) => {
    if (!location || !roomNo) {
      console.log('Missing location or roomNo:', { location, roomNo });
      return;
    }
    
    try {
      console.log('=== LOADING ROOM DATA ===');
      console.log('Location:', location, 'Room:', roomNo);
      
      // Check if we already have the tasks
      const existingTasks = roomsData[location]?.tasks?.[roomNo];
      console.log('Existing tasks:', existingTasks);
      
      // Only fetch if we don't already have the data
      if (!existingTasks || existingTasks.length === 0) {
        console.log('Fetching tasks for room:', roomNo);
        const tasksResponse = await fetch(`${API_URL}/api/get-room-tasks/${roomNo}`);
        if (tasksResponse.ok) {
          const tasks = await tasksResponse.json();
          console.log('Tasks API response:', tasks);
          
          setRoomsData(prev => {
            const updated = {
              ...prev,
              [location]: {
                ...prev[location],
                tasks: {
                  ...(prev[location]?.tasks || {}),
                  [roomNo]: tasks
                }
              }
            };
            console.log('Updated roomsData with tasks:', updated);
            return updated;
          });
        }
      }
      
      // Check if we already have the workers
      const existingWorkers = roomsData[location]?.workers?.[roomNo];
      console.log('Existing workers:', existingWorkers);
      
      if (!existingWorkers || existingWorkers.length === 0) {
        console.log('Fetching workers for room:', roomNo);
        const workersResponse = await fetch(`${API_URL}/api/get-room-workers/${roomNo}`);
        if (workersResponse.ok) {
          const workers = await workersResponse.json();
          console.log('Workers API response:', workers);
          
          setRoomsData(prev => {
            const updated = {
              ...prev,
              [location]: {
                ...prev[location],
                workers: {
                  ...(prev[location]?.workers || {}),
                  [roomNo]: workers
                }
              }
            };
            console.log('Updated roomsData with workers:', updated);
            return updated;
          });
        }
      }
    } catch (error) {
      console.error('Error loading room data:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const removeNewTask = (taskId) => {
    setEditData(prev => ({
      ...prev,
      newTasks: prev.newTasks.filter(task => task.id !== taskId)
    }));
  };

  const handleSaveChanges = async () => {
    setIsProcessing(true);
    
    try {
      console.log('🚀 Starting save changes process...');
      console.log('📝 Edit data:', editData);
      
      // Validate that new tasks have required fields
      const validNewTasks = editData.newTasks.filter(task => {
        const isValid = task.roomNo && task.roomTask && task.roomNo !== '';
        if (!isValid) {
          console.warn('Invalid task found:', task);
        }
        return isValid;
      });
      
      console.log('✅ Valid new tasks:', validNewTasks);
      
      // Prepare the data for saving
      const editPayload = {
        orderNo: orderData.orderDetails.orderNo,
        repairItems: editData.repairItems,
        newTasks: validNewTasks,
        editNotes: editData.notes,
        enteredByUser: currentUser?.username || currentUser?.UserName || 'Unknown' 
      };
      
      console.log('📤 Sending edit data:', editPayload);
      
      // You'll need to create this endpoint or modify existing ones
      const response = await fetch(`${API_URL}/api/edit-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editPayload)
      });
      
      console.log('📡 Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Edit processing failed:', errorText);
        throw new Error(`Edit processing failed: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ Edit processing result:', result);
      
      if (!result.success) {
        throw new Error(result.error || 'Unknown error occurred');
      }
      
      // Prepare complete edit data for parent callback
      const completeEditData = {
        ...editData,
        success: true,
        results: result.results,
        errors: result.errors
      };
      
      // Call parent callback to refresh data
      if (onEditComplete) {
        console.log('🔄 Calling parent callback to refresh data...');
        await onEditComplete(completeEditData);
      }
      
      // Close modal
      onClose();
      
      // Show success message
      const { repairItemsAdded = 0, newTasksAdded = 0, totalErrors = 0 } = result.results || {};
      
      let successMessage = `Order edited successfully!\n\n`;
      successMessage += `• ${repairItemsAdded} repair item(s) added to order\n`;
      successMessage += `• ${newTasksAdded} task(s) added to production schedule`;
      
      if (totalErrors > 0) {
        successMessage += `\n\nNote: ${totalErrors} error(s) occurred during processing. Check console for details.`;
      }
      
      toast.success(successMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
    });
      
    } catch (error) {
      console.error('❌ Error saving changes:', error);
      console.error('❌ Error stack:', error.stack);
      toast.error('Error saving changes: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderRepairsTab = () => (
    <div>
      {/* Custom Repair Input */}
      <div style={{
        background: 'var(--dark-surface)',
        border: '1px solid var(--dark-border)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h6 style={{
          color: 'var(--text-primary)',
          fontSize: '1.2rem',
          fontWeight: '600',
          marginBottom: '1rem'
        }}>
          Add Custom Repair:
        </h6>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gap: '1rem',
          alignItems: 'end'
        }}>
          <input
            type="text"
            style={{
              background: 'var(--dark-elevated)',
              border: '2px solid var(--dark-border)',
              borderRadius: '8px',
              padding: '0.875rem 1rem',
              color: 'var(--text-primary)',
              fontSize: '1.1rem',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              minWidth: 0
            }}
            placeholder="Enter custom repair description..."
            value={editData.customRepair?.description || ''}
            onChange={(e) => setEditData(prev => ({
              ...prev,
              customRepair: {
                ...prev.customRepair,
                description: e.target.value
              }
            }))}
          />
          <input
            type="number"
            step="0.01"
            style={{
              background: 'var(--dark-elevated)',
              border: '2px solid var(--dark-border)',
              borderRadius: '8px',
              padding: '0.875rem 1rem',
              color: 'var(--text-primary)',
              fontSize: '1.1rem',
              width: '120px',
              fontFamily: 'inherit',
              boxSizing: 'border-box'
            }}
            placeholder="Price"
            value={editData.customRepair?.price || ''}
            onChange={(e) => setEditData(prev => ({
              ...prev,
              customRepair: {
                ...prev.customRepair,
                price: e.target.value
              }
            }))}
          />
          <button
            style={{
              padding: '0.875rem 1.5rem',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: '600',
              border: 'none',
              cursor: (!editData.customRepair?.description || !editData.customRepair?.price) ? 'not-allowed' : 'pointer',
              background: (!editData.customRepair?.description || !editData.customRepair?.price) ? 'var(--dark-border)' : 'linear-gradient(135deg, var(--green-accent), var(--green-light))',
              color: 'white',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              opacity: (!editData.customRepair?.description || !editData.customRepair?.price) ? 0.5 : 1
            }}
            onClick={() => {
              if (editData.customRepair?.description && editData.customRepair?.price) {
                addRepairItem({
                  RepairDescription: editData.customRepair.description,
                  RepairCost: parseFloat(editData.customRepair.price)
                });
                setEditData(prev => ({
                  ...prev,
                  customRepair: { description: '', price: '' }
                }));
              }
            }}
            disabled={!editData.customRepair?.description || !editData.customRepair?.price}
          >
            Add
          </button>
        </div>
      </div>

      {/* Pre-defined Repairs */}
      <div style={{
        background: 'var(--dark-surface)',
        border: '1px solid var(--dark-border)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h6 style={{
          color: 'var(--text-primary)',
          fontSize: '1.2rem',
          fontWeight: '600',
          marginBottom: '1rem'
        }}>
          Pre-defined Repairs:
        </h6>

        {/* Search Bar */}
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="Search repairs by description or price..."
            value={repairSearchQuery}
            onChange={(e) => setRepairSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              fontSize: '1rem',
              border: '1px solid var(--dark-border)',
              borderRadius: '8px',
              background: 'var(--dark-elevated)',
              color: 'var(--text-primary)',
              outline: 'none',
              transition: 'border-color 0.3s ease',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--purple-accent)'}
            onBlur={(e) => e.target.style.borderColor = 'var(--dark-border)'}
          />
          {repairSearchQuery && (
            <div style={{
              marginTop: '0.5rem',
              fontSize: '0.9rem',
              color: 'var(--text-secondary)'
            }}>
              Found {filteredRepairOptions.length} repair{filteredRepairOptions.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '1rem',
          maxHeight: '300px',
          overflowY: 'auto',
          padding: '0.5rem'
        }}>
          {filteredRepairOptions.length > 0 ? (
            filteredRepairOptions.map((repair, index) => (
            <div key={index} style={{
              background: 'var(--dark-elevated)',
              border: '1px solid var(--dark-border)',
              borderRadius: '8px',
              padding: '1rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'all 0.3s ease'
            }}>
              <div>
                <div style={{ 
                  fontWeight: '600', 
                  color: 'var(--text-primary)', 
                  fontSize: '1.1rem',
                  marginBottom: '0.25rem'
                }}>
                  {repair.RepairDescription}
                </div>
                <div style={{ 
                  color: 'var(--text-secondary)', 
                  fontSize: '1rem' 
                }}>
                  ${repair.RepairCost.toFixed(2)}
                </div>
              </div>
              <button
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, var(--purple-accent), var(--purple-light))',
                  color: 'white',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                }}
                onClick={() => addRepairItem(repair)}
              >
                Add
              </button>
            </div>
            ))
          ) : (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '2rem',
              color: 'var(--text-secondary)',
              fontSize: '1rem'
            }}>
              No repairs found matching "{repairSearchQuery}"
            </div>
          )}
        </div>
      </div>

      {/* Selected Repairs */}
      <div style={{
        background: 'var(--dark-surface)',
        border: '1px solid var(--dark-border)',
        borderRadius: '12px',
        padding: '1.5rem'
      }}>
        <h6 style={{
          color: 'var(--text-primary)',
          fontSize: '1.2rem',
          fontWeight: '600',
          marginBottom: '1rem'
        }}>
          Selected Repairs:
        </h6>
        {editData.repairItems.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {editData.repairItems.map((item) => (
              <div key={item.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                background: 'rgba(0, 230, 118, 0.1)',
                border: '2px solid var(--green-accent)',
                borderRadius: '8px'
              }}>
                <span style={{ 
                  color: 'var(--text-primary)', 
                  fontSize: '1.1rem' 
                }}>
                  {item.description}
                </span>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem' 
                }}>
                  <span style={{ 
                    color: 'var(--green-light)', 
                    fontWeight: '600', 
                    fontSize: '1.1rem' 
                  }}>
                    ${item.price.toFixed(2)}
                  </span>
                  <button
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '6px',
                      fontSize: '1.2rem',
                      fontWeight: '600',
                      border: 'none',
                      cursor: 'pointer',
                      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                      color: 'white',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                    }}
                    onClick={() => removeRepairItem(item.id)}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
            <div style={{ 
              textAlign: 'right', 
              fontWeight: '700', 
              fontSize: '1.3rem', 
              color: 'var(--green-accent)',
              marginTop: '1rem',
              padding: '1rem',
              background: 'var(--dark-elevated)',
              borderRadius: '8px'
            }}>
              Total Repairs: ${editData.repairItems.reduce((sum, item) => sum + item.price, 0).toFixed(2)}
            </div>
          </div>
        ) : (
          <div style={{ 
            color: 'var(--text-secondary)', 
            fontStyle: 'italic', 
            fontSize: '1.1rem',
            textAlign: 'center',
            padding: '2rem',
            background: 'var(--dark-elevated)',
            borderRadius: '8px'
          }}>
            No repairs selected
          </div>
        )}
      </div>
    </div>
  );

  const renderTasksTab = () => (
    <div>
      <div style={{
        background: 'var(--dark-surface)',
        border: '1px solid var(--dark-border)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1.5rem' 
        }}>
          <h6 style={{
            color: 'var(--text-primary)',
            fontSize: '1.2rem',
            fontWeight: '600',
            margin: 0
          }}>
            Additional Tasks:
          </h6>
          <button 
            style={{
              padding: '0.875rem 1.5rem',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, var(--green-accent), var(--green-light))',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}
            onClick={addNewTask}
          >
            <span>+</span>
            Add Task
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {editData.newTasks.map((task, index) => (
            <div key={task.id} style={{
              background: 'var(--dark-elevated)',
              border: '2px solid var(--dark-border)',
              borderRadius: '12px',
              padding: '1.5rem',
              transition: 'all 0.3s ease'
            }}>
              {/* First row: Location, Room, Task, Delete */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr auto',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <select
                  style={{
                    background: 'var(--dark-elevated)',
                    border: '2px solid var(--dark-border)',
                    borderRadius: '8px',
                    padding: '0.875rem 1rem',
                    color: 'var(--text-primary)',
                    fontSize: '1.1rem',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    minWidth: 0
                  }}
                  value={task.location}
                  onChange={(e) => {
                    handleRoomChange(task.id, e.target.value, ''); // Clear room when location changes
                  }}
                >
                  <option value="">Select Location</option>
                  <option value="Pomona">Pomona</option>
                  <option value="Lakewood">Lakewood</option>
                  <option value="Israel">Israel</option>
                  <option value="China">China</option>
                </select>
                
                <select
                  style={{
                    background: 'var(--dark-elevated)',
                    border: '2px solid var(--dark-border)',
                    borderRadius: '8px',
                    padding: '0.875rem 1rem',
                    color: 'var(--text-primary)',
                    fontSize: '1.1rem',
                    fontFamily: 'inherit',
                    opacity: !task.location ? 0.5 : 1,
                    cursor: !task.location ? 'not-allowed' : 'pointer',
                    boxSizing: 'border-box',
                    minWidth: 0
                  }}
                  value={task.roomNo}
                  onChange={(e) => handleRoomChange(task.id, task.location, e.target.value)}
                  disabled={!task.location}
                >
                  <option value="">Select Room</option>
                  {roomsData[task.location]?.rooms.map(room => (
                    <option key={room.roomNo} value={room.roomNo}>
                      {room.roomName}
                    </option>
                  ))}
                </select>
                
                <select
                  style={{
                    background: 'var(--dark-elevated)',
                    border: '2px solid var(--dark-border)',
                    borderRadius: '8px',
                    padding: '0.875rem 1rem',
                    color: 'var(--text-primary)',
                    fontSize: '1.1rem',
                    fontFamily: 'inherit',
                    opacity: !task.roomNo ? 0.5 : 1,
                    cursor: !task.roomNo ? 'not-allowed' : 'pointer',
                    boxSizing: 'border-box',
                    minWidth: 0
                  }}
                  value={task.roomTask}
                  onChange={(e) => updateNewTask(task.id, { roomTask: e.target.value })}
                  disabled={!task.roomNo}
                >
                  <option value="">Select Task</option>
                  {(() => {
                    const location = task.location;
                    const roomNo = task.roomNo;
                    const tasks = roomsData[location]?.tasks?.[roomNo] || [];
                    
                    if (tasks.length === 0) {
                      return <option value="" disabled>No tasks available for this room</option>;
                    }
                    
                    return tasks.map((roomTask, taskIndex) => {
                      // Try multiple possible property names
                      const taskDescription = 
                        roomTask.TaskDescription || 
                        roomTask.taskDescription || 
                        roomTask.Description || 
                        roomTask.description ||
                        roomTask.RoomTask ||
                        roomTask.roomTask ||
                        roomTask.Task ||
                        roomTask.task ||
                        roomTask.Name ||
                        roomTask.name ||
                        roomTask.Title ||
                        roomTask.title ||
                        Object.values(roomTask).find(value => typeof value === 'string' && value.trim().length > 0) ||
                        `Task ${taskIndex + 1}`;
                      
                      return (
                        <option key={taskIndex} value={taskDescription}>
                          {taskDescription}
                        </option>
                      );
                    });
                  })()}
                </select>
                
                <button
                  style={{
                    padding: '0.75rem',
                    borderRadius: '8px',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    width: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => removeNewTask(task.id)}
                >
                  🗑️
                </button>
              </div>
              
              {/* Second row: Worker, Date, Notes */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '1rem'
              }}>
                <select
                  style={{
                    background: 'var(--dark-elevated)',
                    border: '2px solid var(--dark-border)',
                    borderRadius: '8px',
                    padding: '0.875rem 1rem',
                    color: 'var(--text-primary)',
                    fontSize: '1.1rem',
                    fontFamily: 'inherit',
                    opacity: !task.roomNo ? 0.5 : 1,
                    cursor: !task.roomNo ? 'not-allowed' : 'pointer',
                    boxSizing: 'border-box',
                    minWidth: 0
                  }}
                  value={task.assignedWorker}
                  onChange={(e) => updateNewTask(task.id, { assignedWorker: e.target.value })}
                  disabled={!task.roomNo}
                >
                  <option value="">Select Worker</option>
                  {(() => {
                    const location = task.location;
                    const roomNo = task.roomNo;
                    const workers = roomsData[location]?.workers?.[roomNo] || [];
                    
                    if (workers.length === 0) {
                      return <option value="" disabled>No workers available for this room</option>;
                    }
                    
                    return workers.map((worker, workerIndex) => (
                      <option key={workerIndex} value={worker.userName || worker.UserName}>
                        {worker.UserName || worker.userName}
                      </option>
                    ));
                  })()}
                </select>
                
                <input
                  type="date"
                  style={{
                    background: 'var(--dark-elevated)',
                    border: '2px solid var(--dark-border)',
                    borderRadius: '8px',
                    padding: '0.875rem 1rem',
                    color: 'var(--text-primary)',
                    fontSize: '1.1rem',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    minWidth: 0
                  }}
                  value={task.taskDueDate}
                  onChange={(e) => updateNewTask(task.id, { taskDueDate: e.target.value })}
                />
                
                <textarea
                  style={{
                    background: 'var(--dark-elevated)',
                    border: '2px solid var(--dark-border)',
                    borderRadius: '8px',
                    padding: '0.875rem 1rem',
                    color: 'var(--text-primary)',
                    fontSize: '1.1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: '48px',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    width: '100%'
                  }}
                  placeholder="Notes"
                  rows={1}
                  value={task.taskNotes}
                  onChange={(e) => updateNewTask(task.id, { taskNotes: e.target.value })}
                />
              </div>

              {/* Validation indicator */}
              {(!task.roomNo || !task.roomTask) && (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid #ef4444',
                  color: '#fca5a5',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  marginTop: '1rem',
                  fontSize: '1rem'
                }}>
                  ⚠️ Room and task description are required
                </div>
              )}
            </div>
          ))}

          {editData.newTasks.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem 2rem', 
              background: 'var(--dark-elevated)', 
              borderRadius: '12px',
              border: '2px dashed var(--dark-border)'
            }}>
              <span style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '1.2rem',
                display: 'block'
              }}>
                ℹ️ No additional tasks added yet
              </span>
              <small style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '1rem',
                marginTop: '0.5rem',
                display: 'block'
              }}>
                Click "Add Task" to create new work assignments
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '1rem'
      }}>
        {console.log('🎨 Rendering modal container')}
        <div style={{
          background: 'var(--dark-surface)',
          border: '2px solid var(--dark-border)',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          position: 'relative'
        }}>
          {console.log('🎨 Rendering modal content')}
          <div style={{
            background: 'var(--dark-elevated)',
            borderBottom: '2px solid var(--dark-border)',
            padding: '1.5rem 2rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}>
            <div>
              <h3 style={{
                color: 'var(--text-primary)',
                fontSize: '1.5rem',
                fontWeight: '700',
                margin: 0,
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
              }}>
                Edit Order #{orderData?.orderDetails?.orderNo}
              </h3>
              <small style={{
                color: 'var(--text-secondary)',
                fontSize: '1rem',
                fontWeight: '500',
                marginTop: '0.25rem',
                display: 'block'
              }}>
                Add repairs and tasks
              </small>
            </div>
            <button
              style={{
                background: 'none',
                border: '2px solid var(--dark-border)',
                color: 'var(--text-secondary)',
                fontSize: '2rem',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                lineHeight: 1,
                padding: 0
              }}
              onClick={onClose}
            >
              ×
            </button>
          </div>

          {/* Tab Navigation */}
          <div style={{
            background: 'var(--dark-elevated)',
            borderBottom: '1px solid var(--dark-border)',
            display: 'flex',
            gap: 0
          }}>
            <button
              style={{
                flex: 1,
                padding: '1rem 2rem',
                background: activeTab === 'repairs' ? 'var(--dark-surface)' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'repairs' ? '3px solid var(--green-accent)' : '3px solid transparent',
                color: activeTab === 'repairs' ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onClick={() => setActiveTab('repairs')}
            >
              🔧 Repair Items
              {editData.repairItems.length > 0 && (
                <span style={{
                  background: 'var(--green-accent)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '50%',
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  minWidth: '1.5rem',
                  height: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {editData.repairItems.length}
                </span>
              )}
            </button>
            <button
              style={{
                flex: 1,
                padding: '1rem 2rem',
                background: activeTab === 'tasks' ? 'var(--dark-surface)' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'tasks' ? '3px solid var(--purple-accent)' : '3px solid transparent',
                color: activeTab === 'tasks' ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onClick={() => setActiveTab('tasks')}
            >
              ✓ Room Tasks
              {editData.newTasks.length > 0 && (
                <span style={{
                  background: 'var(--purple-accent)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '50%',
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  minWidth: '1.5rem',
                  height: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {editData.newTasks.length}
                </span>
              )}
            </button>
          </div>

          <div style={{
            padding: '2rem',
            overflowY: 'auto',
            maxHeight: 'calc(90vh - 200px)',
            fontSize: '1.1rem'
          }}>
            {activeTab === 'repairs' ? renderRepairsTab() : renderTasksTab()}

            {/* Additional Notes */}
            <div style={{
              background: 'var(--dark-surface)',
              border: '1px solid var(--dark-border)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginTop: '1.5rem'
            }}>
              <label style={{ 
                color: 'var(--text-primary)', 
                fontSize: '1.1rem', 
                fontWeight: '600', 
                marginBottom: '0.75rem', 
                display: 'block' 
              }}>
                Additional Notes:
              </label>
              <textarea
                style={{
                  background: 'var(--dark-elevated)',
                  border: '2px solid var(--dark-border)',
                  borderRadius: '8px',
                  padding: '0.875rem 1rem',
                  color: 'var(--text-primary)',
                  fontSize: '1.1rem',
                  width: '100%',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
                value={editData.notes}
                onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes about the changes..."
                rows="4"
              />
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '2rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid var(--dark-border)'
            }}>
              <button 
                style={{
                  padding: '0.875rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  border: '2px solid var(--dark-border)',
                  cursor: 'pointer',
                  background: 'var(--dark-elevated)',
                  color: 'var(--text-secondary)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                }}
                onClick={onClose}
              >
                Cancel
              </button>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {/* Summary of changes */}
                {(editData.repairItems.length > 0 || editData.newTasks.length > 0) && (
                  <div style={{
                    color: 'var(--text-secondary)',
                    fontSize: '1rem',
                    textAlign: 'right'
                  }}>
                    {editData.repairItems.length > 0 && (
                      <div>
                        {editData.repairItems.length} repair item{editData.repairItems.length !== 1 ? 's' : ''} 
                        (${editData.repairItems.reduce((sum, item) => sum + item.price, 0).toFixed(2)})
                      </div>
                    )}
                    {editData.newTasks.length > 0 && (
                      <div>
                        {editData.newTasks.filter(t => t.roomNo && t.roomTask).length} task{editData.newTasks.filter(t => t.roomNo && t.roomTask).length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                )}
                
                <button 
                  style={{
                    padding: '0.875rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    border: 'none',
                    cursor: isProcessing || (editData.repairItems.length === 0 && editData.newTasks.filter(t => t.roomNo && t.roomTask).length === 0) ? 'not-allowed' : 'pointer',
                    background: isProcessing ? 'var(--dark-border)' : 
                               (editData.repairItems.length === 0 && editData.newTasks.filter(t => t.roomNo && t.roomTask).length === 0) ? 'var(--dark-border)' : 
                               'linear-gradient(135deg, var(--green-accent), var(--green-light))',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    opacity: (isProcessing || (editData.repairItems.length === 0 && editData.newTasks.filter(t => t.roomNo && t.roomTask).length === 0)) ? 0.5 : 1
                  }}
                  onClick={handleSaveChanges}
                  disabled={isProcessing || (editData.repairItems.length === 0 && editData.newTasks.filter(t => t.roomNo && t.roomTask).length === 0)}
                >
                  {isProcessing ? (
                    <>
                      <span style={{ 
                        display: 'inline-block', 
                        animation: 'spin 1s linear infinite',
                        marginRight: '0.5rem'
                      }}>
                        ⟳
                      </span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <span>💾</span>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default OrderEditModal;